import { classes } from "polytype";
import {
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import type {
  DnsSrvRecord,
  DnsZoneData,
  DnsZoneListItemData,
  DnsZoneListQueryData,
} from "./types.js";
import type { AsyncResourceVariant } from "../../react/index.js";
import { provideReact } from "../../react/index.js";
import { config } from "../../config/config.js";
import type { Project } from "../../project/index.js";
import assertObjectFound from "../../base/assertObjectFound.js";
import { DnsRecordSet } from "../DnsRecordSet/index.js";
import type { DnsRecordSettingsData } from "../DnsRecordSettings/index.js";
import type { DnsRecordMxEntry } from "../DnsRecordMx/index.js";
import type { DnsRecordCaaEntry } from "../DnsRecordCaa/index.js";

export class DnsZone extends ReferenceModel {
  public static ofId(id: string) {
    return new DnsZone(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.dnsZone.find(id);
    if (data !== undefined) {
      return new DnsZoneDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const zone = await this.find(id);
    assertObjectFound(zone, "dnsZone", id);
    return zone;
  });

  public findDetailed = provideReact(
    () => DnsZone.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<DnsZoneDetailed | undefined>>;

  public getDetailed = provideReact(
    () => DnsZone.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<DnsZoneDetailed>>;

  public static query(project: Project, query: DnsZoneListQueryData = {}) {
    return new DnsZoneListQuery(project, query);
  }

  public static async create(name: string, parentZoneId: string) {
    const { id } = await config.behaviors.dnsZone.create(name, parentZoneId);
    return new DnsZone(id);
  }

  public async delete() {
    await config.behaviors.dnsZone.delete(this.id);
  }
  public async setRecordManaged(record: "a" | "mx") {
    await config.behaviors.dnsZone.setRecordManaged(this.id, record);
  }

  public async setCustomARecord(
    a: string[],
    aaaa: string[],
    settings: DnsRecordSettingsData,
  ) {
    await config.behaviors.dnsZone.setARecord(this.id, a, aaaa, settings);
  }

  public async setCustomMxRecord(
    records: DnsRecordMxEntry[],
    settings: DnsRecordSettingsData,
  ) {
    await config.behaviors.dnsZone.setMxRecord(this.id, records, settings);
  }

  public async setTxtRecord(
    entries: string[],
    settings: DnsRecordSettingsData,
  ) {
    await config.behaviors.dnsZone.setTxtRecord(this.id, entries, settings);
  }

  public async setSrvRecord(
    entries: DnsSrvRecord[],
    settings?: DnsRecordSettingsData,
  ) {
    await config.behaviors.dnsZone.setSrvRecord(this.id, entries, settings);
  }

  public async setCname(fqdn?: string, settings?: DnsRecordSettingsData) {
    await config.behaviors.dnsZone.setCname(this.id, fqdn, settings);
  }

  public async setCaaRecord(
    records: DnsRecordCaaEntry[],
    settings?: DnsRecordSettingsData,
  ) {
    await config.behaviors.dnsZone.setCaaRecord(this.id, records, settings);
  }

  public async addCname(
    project: Project,
    hostname: string,
    fqdn: string,
    settings: DnsRecordSettingsData,
  ) {
    const existingZones = await DnsZone.query(project, {
      domain: hostname,
    }).execute();
    const existingZone = existingZones.items.find((i) => i.domain === hostname);
    if (existingZone) {
      await existingZone.setCname(fqdn, settings);
      return;
    }
    const newZone = await DnsZone.create(hostname, this.id);
    await newZone.setCname(fqdn, settings);
  }

  public async removeCname() {
    await config.behaviors.dnsZone.removeCname(this.id);
  }
}

export class DnsZoneCommon extends classes(DataModel<DnsZoneData>, DnsZone) {
  public readonly recordSet: DnsRecordSet;
  public readonly domain: string;
  public constructor(data: DnsZoneData) {
    super([data], [data.id]);
    this.domain = data.domain;
    this.recordSet = new DnsRecordSet(this, data.recordSet);
  }
}

export class DnsZoneDetailed extends classes(
  DnsZoneCommon,
  DataModel<DnsZoneData>,
) {
  public constructor(data: DnsZoneData) {
    super([data], [data]);
  }
}

export class DnsZoneListQuery extends ListQueryModel<DnsZoneListQueryData> {
  public readonly project: Project;
  public constructor(project: Project, query: DnsZoneListQueryData = {}) {
    super(query, { dependencies: [project.id] });
    this.project = project;
  }

  public refine(query: DnsZoneListQueryData = {}) {
    return new DnsZoneListQuery(this.project, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { domain } = this.query;
    const { items, totalCount } = await config.behaviors.dnsZone.query(
      this.project.id,
    );
    // List Zones does not support a domain query parameter, so filtering is needed
    const zones = domain
      ? items.filter((i) => i.domain.endsWith(domain))
      : items;
    return new DnsZoneList(
      this.project,
      this.query,
      zones.map((z) => new DnsZoneListItem(z)),
      totalCount,
    );
  }, [this.queryId, "dnsZone"]);
}

export class DnsZoneListItem extends classes(
  DnsZoneCommon,
  DataModel<DnsZoneListItemData>,
) {
  public constructor(data: DnsZoneListItemData) {
    super([data], [data]);
  }
}

export class DnsZoneList extends classes(
  DnsZoneListQuery,
  ListDataModel<DnsZoneListItem>,
) {
  public constructor(
    project: Project,
    query: DnsZoneListQueryData,
    zones: DnsZoneListItem[],
    totalCount: number,
  ) {
    super([project, query], [zones, totalCount]);
  }
}
